import React, { useState, useEffect } from 'react';
import { getDimensions, createDimension, updateDimension } from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import Button from '../../components/Button';
import Alert from '../../components/Alert';
import { useAuth } from '../../context/AuthContext';

// V4 F.1: 维度可配置 CRUD 管理页
// - code 创建后不可改 (后端 PATCH 拦截)
// - 删除已禁 (后端返回 405), 改用 is_active=false 软停用
// - reorder: 表单内直接改 sort_order (拖拽 P2 再做)

const EMPTY_FORM = {
  code: '',
  name: '',
  description: '',
  weight: 1.0,
  sort_order: 0,
  is_active: true,
};

// code 正则: ^[a-z][a-z0-9_]{1,38}[a-z0-9]$
const CODE_PATTERN = /^[a-z][a-z0-9_]{1,38}[a-z0-9]$/;

export default function AdminDimensionsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState(null); // null=关闭, 'new'=新建, number=编辑
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const isCreating = editingId === 'new';

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getDimensions();
      setList(Array.isArray(data) ? data : (data.results ?? []));
    } catch { setError('加载失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditingId('new');
    setError('');
    setSuccess('');
  };
  const openEdit = (d) => {
    setForm({
      code: d.code,
      name: d.name,
      description: d.description,
      weight: d.weight,
      sort_order: d.sort_order,
      is_active: d.is_active,
    });
    setEditingId(d.id);
    setError('');
    setSuccess('');
  };
  const closeForm = () => { setEditingId(null); setForm(EMPTY_FORM); };

  const validate = () => {
    if (!form.name.trim()) return '维度名称不能为空';
    if (isCreating) {
      if (!form.code.trim()) return 'code 不能为空（创建后不可修改）';
      if (!CODE_PATTERN.test(form.code)) {
        return 'code 格式: 小写字母+数字+下划线，开头字母，长度 3~40';
      }
    }
    if (form.weight < 0 || form.weight > 10) return '权重必须在 0~10 之间';
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setSaving(true);
    setError('');
    try {
      if (isCreating) {
        await createDimension(form);
        setSuccess(`维度已创建：${form.code}`);
      } else {
        // 编辑时不传 code (后端会拦, 前端先剥)
        const { code: _ignored, ...payload } = form;
        await updateDimension(editingId, payload);
        setSuccess('已保存');
      }
      closeForm();
      load();
    } catch (e) {
      const data = e.response?.data || {};
      const msg = data.code?.[0] || data.name?.[0] || data.detail || '保存失败';
      setError(msg);
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (d) => {
    try {
      await updateDimension(d.id, { is_active: !d.is_active });
      setSuccess(`已${d.is_active ? '停用' : '启用'}：${d.name}`);
      load();
    } catch { setError('操作失败'); }
  };

  return (
    <AdminLayout title="维度管理">
      {error && <Alert type="error" style={{ marginBottom: 12 }}>{error}</Alert>}
      {success && <Alert type="success" style={{ marginBottom: 12 }}>{success}</Alert>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: '#94a3b8' }}>
          共 {list.length} 个维度
          {!isSuperAdmin && '（只读，仅超管可编辑）'}
        </span>
        {isSuperAdmin && (
          <Button onClick={openNew}>+ 新增维度</Button>
        )}
      </div>

      {/* 新建/编辑表单 */}
      {editingId !== null && (
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
          padding: 24, marginBottom: 20,
        }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1a3a6b', marginBottom: 16 }}>
            {isCreating ? '新增维度' : `编辑维度：${form.name}`}
          </h3>

          {/* code + name 两栏 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>
                code <span style={{ color: '#dc2626' }}>*</span>
                {!isCreating && <span style={{ color: '#94a3b8', fontWeight: 400 }}>（不可改）</span>}
              </label>
              <input
                style={{
                  ...inputStyle,
                  ...(isCreating ? {} : codeReadonlyStyle),
                  fontFamily: 'ui-monospace, monospace',
                }}
                value={form.code}
                disabled={!isCreating}
                onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                placeholder={isCreating ? '如：prompt_skill' : ''}
              />
              {isCreating && (
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                  小写英文 + 数字 + 下划线, 创建后不可改
                </span>
              )}
            </div>
            <div>
              <label style={labelStyle}>维度名称（可改） <span style={{ color: '#dc2626' }}>*</span></label>
              <input style={inputStyle} value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="如：AI指令清晰度" />
            </div>
          </div>

          {/* 权重 + 排序 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>权重（0~10）</label>
              <input style={inputStyle} type="number" min="0" max="10" step="0.1" value={form.weight}
                onChange={e => setForm(f => ({ ...f, weight: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div>
              <label style={labelStyle}>排序（数字越小越前）</label>
              <input style={inputStyle} type="number" min="0" value={form.sort_order}
                onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>

          {/* 说明 */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>维度说明</label>
            <textarea style={{ ...inputStyle, height: 72, resize: 'vertical' }} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="该维度的评测方向说明，会展示在用户报告页" />
          </div>

          {/* is_active */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <input type="checkbox" id="is_active" checked={form.is_active}
              onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
            <label htmlFor="is_active" style={{ fontSize: 13, color: '#475569', cursor: 'pointer' }}>
              启用该维度（停用后不参与新测评，但老报告原样保留）
            </label>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <Button onClick={handleSave} disabled={saving}>{saving ? '保存中...' : '保存'}</Button>
            <Button variant="secondary" onClick={closeForm}>取消</Button>
          </div>
        </div>
      )}

      {/* 维度列表 */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>加载中...</div>
        ) : list.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>暂无维度</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['排序', 'code', '维度名称', '说明', '权重', '题目数', '状态', ...(isSuperAdmin ? ['操作'] : [])].map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left', fontWeight: 600,
                    color: '#475569', borderBottom: '1px solid #e2e8f0',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((d, idx) => (
                <tr key={d.id} style={{
                  borderBottom: idx < list.length - 1 ? '1px solid #f1f5f9' : 'none',
                  background: editingId === d.id ? '#f0f7ff' : 'transparent',
                  opacity: d.is_active ? 1 : 0.55,
                }}>
                  <td style={tdStyle}>{d.sort_order}</td>
                  <td style={{
                    ...tdStyle,
                    fontFamily: 'ui-monospace, monospace',
                    color: '#64748b',
                    background: '#f8fafc',
                    fontSize: 12,
                  }}>{d.code}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#1a3a6b' }}>{d.name}</td>
                  <td style={{ ...tdStyle, color: '#64748b', maxWidth: 280 }}>
                    <span style={{
                      display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                      {d.description || '—'}
                    </span>
                  </td>
                  <td style={tdStyle}>{d.weight}</td>
                  <td style={tdStyle}>{d.question_count}</td>
                  <td style={tdStyle}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                      fontSize: 12, fontWeight: 600,
                      background: d.is_active ? '#dcfce7' : '#f1f5f9',
                      color: d.is_active ? '#16a34a' : '#94a3b8',
                    }}>
                      {d.is_active ? '启用' : '停用'}
                    </span>
                  </td>
                  {isSuperAdmin && (
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => openEdit(d)} style={btnStyle('#2d5aa0')}>编辑</button>
                        <button
                          onClick={() => handleToggleActive(d)}
                          style={btnStyle(d.is_active ? '#94a3b8' : '#16a34a')}
                        >
                          {d.is_active ? '停用' : '启用'}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!isSuperAdmin && (
        <p style={{ marginTop: 12, fontSize: 12, color: '#94a3b8' }}>只有超级管理员可以编辑维度。</p>
      )}
    </AdminLayout>
  );
}

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 };
const inputStyle = {
  width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8,
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const codeReadonlyStyle = {
  background: '#f1f5f9',
  color: '#94a3b8',
  cursor: 'not-allowed',
};
const tdStyle = { padding: '12px 14px', verticalAlign: 'middle', color: '#334155' };
const btnStyle = (color) => ({
  padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
  background: color, color: '#fff', fontSize: 12, fontWeight: 600,
});
